import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../hooks/useToast';
import { AdminPlanFeature, PlanSection, plansApi } from '../../../api/plans';

type ValueType = AdminPlanFeature['valueType'];

const TYPES: Array<{ value: ValueType; label: string; hint: string }> = [
  { value: 'boolean', label: 'On or off', hint: 'Included or not, like "Profit and loss".' },
  { value: 'number', label: 'A limit', hint: 'A number per plan, or unlimited, like "Team seats".' },
  { value: 'enum', label: 'A choice', hint: 'One of a few options, like "Basic, Full". List the most generous last.' },
  { value: 'text', label: 'Text', hint: 'Free words per plan, like "Email support".' },
];

const KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

const fieldStyle: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 10px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
};

/**
 * Add a feature to a section, or change one.
 *
 * The key and the kind of value are fixed once a feature exists: code that
 * enforces a feature asks for it by key and reads a value of that kind, so
 * changing either would quietly break the check.
 */
export const FeatureEditor: React.FC<{
  open: boolean;
  feature: AdminPlanFeature | null;
  sections: PlanSection[];
  defaultSectionId: string | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, feature, sections, defaultSectionId, onClose, onSaved }) => {
  const toast = useToast();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [hint, setHint] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [valueType, setValueType] = useState<ValueType>('boolean');
  const [unit, setUnit] = useState('');
  const [relativeToFree, setRelativeToFree] = useState(false);
  const [options, setOptions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setKey(feature?.key ?? '');
    setLabel(feature?.label ?? '');
    setHint(feature?.hint ?? '');
    setSectionId(feature?.sectionId ?? defaultSectionId ?? sections[0]?.id ?? '');
    setValueType(feature?.valueType ?? 'boolean');
    setUnit(feature?.unit ?? '');
    setRelativeToFree(feature?.relativeToFree ?? false);
    setOptions(feature?.enumOptions?.join(', ') ?? '');
  }, [open, feature, defaultSectionId, sections]);

  const editing = !!feature;
  const cleanOptions = options
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)
    .join(',');

  async function save() {
    if (!label.trim()) return setError('Give it a name.');
    if (!sectionId) return setError('Choose a section.');
    if (!editing && !KEY.test(key.trim())) {
      return setError('The key is lower case with a dot, like "support.phone".');
    }
    if (valueType === 'enum' && cleanOptions.split(',').filter(Boolean).length < 2) {
      return setError('A choice needs at least two options, separated by commas.');
    }
    setError(null);
    setSaving(true);
    try {
      if (editing && feature) {
        await plansApi.updateFeature(feature.id, {
          label: label.trim(),
          hint: hint.trim(),
          sectionId,
          ...(valueType === 'number' ? { unit: unit.trim(), relativeToFree } : {}),
          ...(valueType === 'enum' ? { enumOptions: cleanOptions } : {}),
        });
        toast.success(`${label.trim()} saved.`);
      } else {
        await plansApi.createFeature({
          key: key.trim(),
          label: label.trim(),
          ...(hint.trim() ? { hint: hint.trim() } : {}),
          sectionId,
          valueType,
          ...(valueType === 'number' && unit.trim() ? { unit: unit.trim() } : {}),
          ...(valueType === 'number' && relativeToFree ? { relativeToFree } : {}),
          ...(valueType === 'enum' ? { enumOptions: cleanOptions } : {}),
        });
        toast.success(`${label.trim()} added. It is off on every plan until you switch it on.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err, 'Could not save that feature.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={onClose} title={editing ? 'Edit feature' : 'Add a feature'} maxWidth={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="Name" value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
        <Input
          label="Hint (optional)"
          value={hint}
          maxLength={240}
          hint="A short line under the name in the app."
          onChange={(e) => setHint(e.target.value)}
        />
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
          Section
          <select aria-label="Section" value={sectionId} onChange={(e) => setSectionId(e.target.value)} style={fieldStyle}>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        {editing ? (
          <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            Key <code>{feature?.key}</code> · {TYPES.find((t) => t.value === valueType)?.label}. These stay fixed, because
            the app checks the feature by its key.
          </div>
        ) : (
          <>
            <Input
              label="Key"
              value={key}
              maxLength={80}
              placeholder="support.phone"
              hint="How the app refers to it. Lower case, with a dot. It cannot be changed later."
              onChange={(e) => setKey(e.target.value.toLowerCase())}
            />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
              Kind of value
              <select
                aria-label="Kind of value"
                value={valueType}
                onChange={(e) => setValueType(e.target.value as ValueType)}
                style={fieldStyle}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--neutral-600)' }}>
                {TYPES.find((t) => t.value === valueType)?.hint}
              </span>
            </label>
          </>
        )}

        {valueType === 'number' && (
          <Input
            label="Unit (optional)"
            value={unit}
            maxLength={24}
            placeholder="seats"
            onChange={(e) => setUnit(e.target.value)}
          />
        )}
        {valueType === 'number' && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--neutral-700)' }}>
            <input
              type="checkbox"
              checked={relativeToFree}
              onChange={(e) => setRelativeToFree(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              Show against Free
              <span style={{ display: 'block', fontSize: 12, color: 'var(--neutral-500)' }}>
                The apps show each plan as a multiple of the free plan, like “5× Free”, instead of the number. For
                allowances whose number means nothing to a customer, such as Ibra usage.
              </span>
            </span>
          </label>
        )}
        {valueType === 'enum' && (
          <Input
            label="Options"
            value={options}
            placeholder="Basic, Full"
            hint="Separated by commas, most generous last."
            onChange={(e) => setOptions(e.target.value)}
          />
        )}

        {!editing && (
          <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            A new feature shows in the app&apos;s plan comparison. It only limits what a business can do once the app
            checks it, which needs a developer.
          </div>
        )}

        {error && <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={saving}>
            {editing ? 'Save' : 'Add feature'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
